import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  MenuItem,
  Box,
  Stack,
  CircularProgress,
  InputAdornment,
  IconButton,
  Switch,
  FormControlLabel
} from '@mui/material';
import { X, CreditCard, Receipt, Ban } from 'lucide-react';

const MEMBER_TYPES = [
  { value: 'courier', label: 'Kuriiri' },
  { value: 'sender',  label: 'Lähettäjä' },
  { value: 'any',     label: 'Molemmat' },
];

const PAYMENT_METHODS = [
  { value: 'email', label: 'Sähköposti' },
  { value: 'bank',  label: 'Pankkisiirto' },
];

const emptyPayment = { noPayment: false, frequency: '7', method: 'email', iban: '' };
const emptyBilling = { noBilling: false, frequency: '7', billingFee: '5', method: 'email' };

/* ── Design primitives ── */

function SectionLabel({ icon: Icon, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {Icon && <Icon size={13} opacity={0.45} />}
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontSize: '0.68rem' }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function SectionCard({ children, dimmed = false }) {
  return (
    <Box
      sx={{
        border: '0.5px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        p: 2,
        opacity: dimmed ? 0.45 : 1,
        transition: 'opacity 0.2s',
      }}
    >
      {children}
    </Box>
  );
}

function Row({ children }) {
  return <Stack direction="row" spacing={1.5}>{children}</Stack>;
}

function OptedOutBanner({ label, onEnable }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'action.hover',
        borderRadius: 1,
        px: 1.5,
        py: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Ban size={13} opacity={0.4} />
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      </Box>
      <Button size="small" variant="text" onClick={onEnable} sx={{ fontSize: '0.7rem' }}>
        Lisää tiedot
      </Button>
    </Box>
  );
}

/* ── Main export ── */
export const MemberEditModal = ({ open, member, onClose, onSave, loading }) => {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (member) {
      setForm({
        name:       member.name       || '',
        email:      member.email      || '',
        phone:      member.phone      || '',
        yNumber:    member.yNumber    || '',
        memberType: member.memberType || 'any',
        paymentDetails: member.paymentDetails
          ? { ...emptyPayment, ...member.paymentDetails }
          : null,
        billingDetails: member.billingDetails
          ? { ...emptyBilling, ...member.billingDetails }
          : null,
      });
    }
  }, [member]);

  if (!form) return null;

  const set       = (field)          => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
  const setNested = (section, field) => (e) => setForm((p) => ({ ...p, [section]: { ...p[section], [field]: e.target.value } }));

  const cycleSection = (section, noKey, empty) =>
    setForm((p) => {
      const cur = p[section];
      if (cur === null) return { ...p, [section]: { [noKey]: true } };
      if (cur[noKey])   return { ...p, [section]: { ...empty, [noKey]: false } };
      return { ...p, [section]: null };
    });

  const handleSave = () => {
    const cleaned = {
      ...form,
      name:    form.name.trim()    || null,
      email:   form.email.trim()   || null,
      phone:   form.phone.trim()   || null,
      yNumber: form.yNumber.trim() || null,
      paymentDetails: form.paymentDetails
        ? form.paymentDetails.noPayment
          ? { noPayment: true }
          : {
              noPayment: false,
              frequency: Number(form.paymentDetails.frequency) || null,
              method:    form.paymentDetails.method,
              iban:      form.paymentDetails.iban.trim() || null,
            }
        : null,
      billingDetails: form.billingDetails
        ? form.billingDetails.noBilling
          ? { noBilling: true }
          : {
              noBilling:  false,
              frequency:  Number(form.billingDetails.frequency)  || null,
              billingFee: Number(form.billingDetails.billingFee) || null,
              method:     form.billingDetails.method,
            }
        : null,
    };
    onSave(member.userId, cleaned);
  };

  const showPayment = form.memberType === 'courier' || form.memberType === 'any';
  const showBilling = form.memberType === 'sender'  || form.memberType === 'any';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>

      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.5 }}>
        <Typography variant="h6" fontWeight={600}>Muokkaa jäsentä</Typography>
        <IconButton size="small" onClick={onClose}><X size={18} /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={3}>

          {/* ── Perustiedot ── */}
          <Box>
            <SectionLabel>Perustiedot</SectionLabel>
            <Stack spacing={1.5} sx={{ mt: 1.5 }}>
              <Row>
                <TextField label="Nimi"          fullWidth size="small" value={form.name}  onChange={set('name')} />
                <TextField label="Puhelinnumero" fullWidth size="small" value={form.phone} onChange={set('phone')} />
              </Row>
              <Row>
                <TextField label="Sähköposti" fullWidth size="small" type="email" value={form.email}   onChange={set('email')} />
                <TextField label="Y-tunnus"   fullWidth size="small"              value={form.yNumber} onChange={set('yNumber')} />
              </Row>
              <TextField label="Jäsentyyppi" fullWidth select size="small" value={form.memberType} onChange={set('memberType')}>
                {MEMBER_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </TextField>
            </Stack>
          </Box>

          {/* ── Maksutiedot ── */}
          {showPayment && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <SectionLabel icon={CreditCard}>Maksutiedot (kuriiri)</SectionLabel>
                <Button
                  size="small" variant="text"
                  color={form.paymentDetails === null ? 'primary' : 'error'}
                  onClick={() => cycleSection('paymentDetails', 'noPayment', emptyPayment)}
                  sx={{ fontSize: '0.7rem' }}
                >
                  {form.paymentDetails === null ? 'Lisää' : 'Poista'}
                </Button>
              </Box>

              {form.paymentDetails === null ? (
                <SectionCard dimmed>
                  <Typography variant="caption" color="text.secondary">Ei maksutietoja</Typography>
                </SectionCard>
              ) : form.paymentDetails.noPayment ? (
                <SectionCard>
                  <OptedOutBanner
                    label="Palkkioden maksaminen poissa"
                    onEnable={() => cycleSection('paymentDetails', 'noPayment', emptyPayment)}
                  />
                </SectionCard>
              ) : (
                <SectionCard>
                  <Stack spacing={1.5}>
                    <Row>
                      <TextField
                        label="Maksuväli (päiviä)" fullWidth type="number" size="small"
                        value={form.paymentDetails.frequency}
                        onChange={setNested('paymentDetails', 'frequency')}
                        inputProps={{ min: 1 }}
                      />
                      <TextField
                        label="Maksutapa" fullWidth select size="small"
                        value={form.paymentDetails.method}
                        onChange={setNested('paymentDetails', 'method')}
                      >
                        {PAYMENT_METHODS.map((m) => (
                          <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                        ))}
                      </TextField>
                    </Row>
                    <Row>
  <TextField
    label="IBAN" fullWidth size="small"
    value={form.paymentDetails.iban}
    onChange={setNested('paymentDetails', 'iban')}
    placeholder="FI00 0000 0000 0000 00"
  />
  <FormControlLabel
    sx={{ flexShrink: 0, alignSelf: 'flex-start', mt: 0.5, mr: 0 }}
    control={
      <Switch
        size="small"
        checked={form.paymentDetails.noPayment !== false}
        onChange={(e) =>
          setForm((p) => ({
            ...p,
            paymentDetails: { ...p.paymentDetails, noPayment: e.target.checked },
          }))
        }
      />
    }
    label={
      <Typography variant="caption" color="text.secondary">
        Maksetaan palkkio
      </Typography>
    }
  />
</Row>
                  </Stack>
                </SectionCard>
              )}
            </Box>
          )}

          {/* ── Laskutustiedot ── */}
          {showBilling && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <SectionLabel icon={Receipt}>Laskutustiedot (lähettäjä)</SectionLabel>
                <Button
                  size="small" variant="text"
                  color={form.billingDetails === null ? 'primary' : 'error'}
                  onClick={() => cycleSection('billingDetails', 'noBilling', emptyBilling)}
                  sx={{ fontSize: '0.7rem' }}
                >
                  {form.billingDetails === null ? 'Lisää' : 'Poista'}
                </Button>
              </Box>

              {form.billingDetails === null ? (
                <SectionCard dimmed>
                  <Typography variant="caption" color="text.secondary">Ei laskutustietoja</Typography>
                </SectionCard>
              ) : form.billingDetails.noBilling ? (
                <SectionCard>
                  <OptedOutBanner
                    label="Ei laskutusta tälle jäsenelle"
                    onEnable={() => cycleSection('billingDetails', 'noBilling', emptyBilling)}
                  />
                </SectionCard>
              ) : (
                <SectionCard>
                  <Stack spacing={1.5}>
                    <Row>
                      <TextField
                        label="Laskutusväli (päiviä)" fullWidth type="number" size="small"
                        value={form.billingDetails.frequency}
                        onChange={setNested('billingDetails', 'frequency')}
                        inputProps={{ min: 1 }}
                      />
                      <TextField
                        label="Laskutusmaksu (€)" fullWidth type="number" size="small"
                        value={form.billingDetails.billingFee}
                        onChange={setNested('billingDetails', 'billingFee')}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
                      />
                    </Row>
                    <Row>
  <TextField
    label="Laskutustapa" fullWidth select size="small"
    value={form.billingDetails.method}
    onChange={setNested('billingDetails', 'method')}
  >
    {PAYMENT_METHODS.map((m) => (
      <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
    ))}
  </TextField>
  <FormControlLabel
    sx={{ flexShrink: 0, alignSelf: 'flex-start', mt: 0.5, mr: 0 }}
    control={
      <Switch
        size="small"
        checked={form.billingDetails.noBilling !== false}
        onChange={(e) =>
          setForm((p) => ({
            ...p,
            billingDetails: { ...p.billingDetails, noBilling: e.target.checked },
          }))
        }
      />
    }
    label={
      <Typography variant="caption" color="text.secondary">
        Laskutetaan
      </Typography>
    }
  />
</Row>
                  </Stack>
                </SectionCard>
              )}
            </Box>
          )}

        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>Peruuta</Button>
        <Button
          variant="contained" onClick={handleSave} disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Tallennetaan...' : 'Tallenna'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};